import { useRef, useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { deleteBuilderAssistantPhoto, uploadBuilderAssistantPhoto } from "@/lib/manufacturing/builderAssistantClient";
import { loadQcObservationPhotos, type QcStoredPhoto } from "@/lib/manufacturing/qcChecklistPhotoRead";
import { QC_FAIL_PHOTO_CAP } from "@/lib/manufacturing/qcPhotoQueue";
import { QcPhotoViewer } from "@/components/manufacturing/qc/QcPhotoViewer";

export function BuilderAssistantFailurePhotos({
  observationId,
  count,
  disabled,
  onChanged,
}: {
  observationId: string;
  count: number;
  disabled: boolean;
  onChanged: () => void;
}) {
  const [photos, setPhotos] = useState<QcStoredPhoto[] | null>(null);
  const [viewer, setViewer] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const open = photos?.[viewer] ?? null;

  async function load() {
    const listed = await loadQcObservationPhotos(observationId, "failure");
    if (!listed.ok) {
      setError(listed.error);
      return;
    }
    if (listed.data.photos.length === 0) {
      setError("No photo is stored yet.");
      return;
    }
    setPhotos(listed.data.photos);
    setViewer(0);
  }

  async function add(file: File) {
    if (busyRef.current || count >= QC_FAIL_PHOTO_CAP) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    const res = await uploadBuilderAssistantPhoto(observationId, file);
    busyRef.current = false;
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onChanged();
  }

  async function remove(evidenceId: string) {
    setBusy(true);
    setError(null);
    const res = await deleteBuilderAssistantPhoto(evidenceId);
    setBusy(false);
    setPhotos(null);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onChanged();
  }

  async function replace(evidenceId: string, file: File) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    const removed = await deleteBuilderAssistantPhoto(evidenceId);
    if (!removed.ok) {
      busyRef.current = false;
      setBusy(false);
      setError(removed.error);
      return;
    }
    const uploaded = await uploadBuilderAssistantPhoto(observationId, file);
    busyRef.current = false;
    setBusy(false);
    setPhotos(null);
    if (!uploaded.ok) setError(uploaded.error);
    onChanged();
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {count > 0 ? (
          <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void load()}>
            {busy && !open ? "Loading photos…" : `Failure images (${count})`}
          </button>
        ) : null}
        {count < QC_FAIL_PHOTO_CAP ? (
          <label className={`${ghostBtnClass} inline-flex cursor-pointer`}>
            {count === 0 ? "Failure photo" : "Add photo"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="hidden"
              disabled={disabled || busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) void add(file);
              }}
            />
          </label>
        ) : (
          <span className="text-sm text-gray-900 dark:text-zinc-100">3 photos</span>
        )}
      </div>
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {open && photos ? (
        <QcPhotoViewer
          urls={photos.map((photo) => photo.url)}
          index={viewer}
          title="Failure photo"
          onClose={() => setPhotos(null)}
          onDelete={
            disabled
              ? undefined
              : (index) => {
                  const id = photos[index]?.id;
                  if (id) void remove(id);
                }
          }
          onReplace={
            disabled
              ? undefined
              : (file, index) => {
                  const id = photos[index]?.id;
                  if (id) void replace(id, file);
                }
          }
        />
      ) : null}
    </div>
  );
}
